import { useCallback } from 'react';
import { api } from '../../lib/api';
import { useRequest } from '../../lib/use-request';
import {
  DEFAULT_BUSINESS,
  DEFAULT_PRODUCTS,
  DEFAULT_SHELF_RULES,
  DEFAULT_STOCK,
  NEEDS,
  NEED_KEYS,
  WEAR_PARTS,
  WEAR_PART_LABELS,
  type WearPart,
  PREMISES_KEYS,
  type BusinessContent,
  type BusinessKind,
  type BusinessRules,
  type NeedKey,
  type ShelfRules,
  type ShopProduct,
} from '@london-life/contracts';
import { Button } from '@london-life/ui/components/button';
import { Input } from '@london-life/ui/components/input';
import { Switch } from '@london-life/ui/components/switch';
import { PageHeader } from '@london-life/ui/admin/page-header';
import { ErrorNotice } from '@london-life/ui/feedback/error-notice';
import { useAdmin } from '../../lib/admin-context';
import { useContent } from './use-content';
import { ContentBar } from './versions';
import { Field, number, toPence, idFor } from './form';

// the character options' category for each part of a look
const WEAR_CATEGORY: Record<WearPart, string> = {
  hair: 'hair',
  hairColour: 'hair_colour',
  facialHair: 'facial_hair',
  outfit: 'outfit',
};
const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

// Businesses (Lagos Life's Company): the kinds players can start, the premises sizes, the areas and their price
// tiers, and the rules (registering, buying premises, selling back, tax, grace for rent). Part of the content
// draft, published with it. The sums players see come from these numbers.
export function BusinessesPage() {
  const { can } = useAdmin();
  const editor = useContent();
  const working = editor.working;
  const readOnly = !can('content.write');
  const business = working?.business ?? DEFAULT_BUSINESS;
  // the character options a product can give (a hairstyle, an outfit…)
  const looks = useRequest(useCallback(() => api.characterOptions(), []));
  const optionsFor = (part: WearPart) =>
    (looks.data?.options ?? []).filter((option) => option.category === WEAR_CATEGORY[part]);
  const change = (fn: (draft: BusinessContent) => void) =>
    editor.edit((content) => {
      content.business ??= structuredClone(DEFAULT_BUSINESS);
      fn(content.business);
    });
  const kind = (id: string, fn: (item: BusinessKind) => void) =>
    change((draft) => {
      const item = draft.kinds.find((entry) => entry.id === id);
      if (item) fn(item);
    });
  const rule = <K extends keyof BusinessRules>(key: K, value: BusinessRules[K]) =>
    change((draft) => void (draft.rules[key] = value));
  const products = business.products ?? DEFAULT_PRODUCTS;
  const shelfRules = business.shelf ?? DEFAULT_SHELF_RULES;
  // content saved before shelves were content gets the starting products and rules the first time
  const changeProducts = (fn: (list: ShopProduct[]) => void) =>
    change((draft) => {
      draft.products ??= structuredClone(DEFAULT_PRODUCTS);
      fn(draft.products);
    });
  const product = (id: string, fn: (item: ShopProduct) => void) =>
    changeProducts((list) => {
      const item = list.find((entry) => entry.id === id);
      if (item) fn(item);
    });
  const shelf = <K extends keyof ShelfRules>(key: K, value: ShelfRules[K]) =>
    change((draft) => {
      draft.shelf ??= structuredClone(DEFAULT_SHELF_RULES);
      draft.shelf[key] = value;
    });
  return (
    <section>
      <PageHeader
        title="Businesses"
        description="What players can start (as Lagos Life's Company): city customers come every evening at 6 pm and the takings go in the till; rent, wages and tax come out on Saturday at 6 pm. Changes are a draft until they are published."
      />
      <ContentBar editor={editor} />
      {editor.failure && <ErrorNotice error={editor.failure} onRetry={() => void editor.reload()} />}
      {!working && !editor.failure && <p className="admin-muted">Loading…</p>}
      {working && (
        <div className="content-editor">
          <fieldset className="admin-panel content-section" disabled={readOnly} aria-label="Rules">
            <h2 className="content-subtitle">Rules</h2>
            <label className="designer-check">
              <Switch
                checked={business.rules.open}
                aria-label="Players can start businesses"
                onCheckedChange={(on) => rule('open', on)}
              />{' '}
              Players can start businesses
            </label>
            <div className="content-grid">
              <Field id="biz-register" label="Registering (£)">
                <Input
                  id="biz-register"
                  type="number"
                  min={0}
                  step="0.01"
                  value={business.rules.registerPence / 100}
                  onChange={(e) => rule('registerPence', toPence(e.target.value, business.rules.registerPence))}
                />
              </Field>
              <Field id="biz-max" label="Businesses each">
                <Input
                  id="biz-max"
                  type="number"
                  min={1}
                  max={10}
                  value={business.rules.maxBusinesses}
                  onChange={(e) => rule('maxBusinesses', Math.round(number(e.target.value, 3)))}
                />
              </Field>
              <Field id="biz-buy" label="Buying premises: weeks of rent">
                <Input
                  id="biz-buy"
                  type="number"
                  min={1}
                  max={520}
                  value={business.rules.buyWeeks}
                  onChange={(e) => rule('buyWeeks', Math.round(number(e.target.value, 40)))}
                />
              </Field>
              <Field id="biz-resale" label="Sold back for (% of cost)">
                <Input
                  id="biz-resale"
                  type="number"
                  min={0}
                  max={100}
                  value={Math.round(business.rules.resale * 100)}
                  onChange={(e) => rule('resale', number(e.target.value, 60) / 100)}
                />
              </Field>
              <Field id="biz-tax" label="Tax on takings (%)">
                <Input
                  id="biz-tax"
                  type="number"
                  min={0}
                  max={90}
                  value={Math.round(business.rules.tax * 100)}
                  onChange={(e) => rule('tax', number(e.target.value, 5) / 100)}
                />
              </Field>
              <Field id="biz-cap" label="Most a player takes an evening (£)">
                <Input
                  id="biz-cap"
                  type="number"
                  min={1}
                  value={business.rules.nightCapPence / 100}
                  onChange={(e) => rule('nightCapPence', toPence(e.target.value, business.rules.nightCapPence))}
                />
              </Field>
              <Field id="biz-grace" label="Days of grace for rent">
                <Input
                  id="biz-grace"
                  type="number"
                  min={0}
                  max={14}
                  value={business.rules.graceDays}
                  onChange={(e) => rule('graceDays', Math.round(number(e.target.value, 3)))}
                />
              </Field>
              <Field id="biz-catch" label="Missed evenings caught up">
                <Input
                  id="biz-catch"
                  type="number"
                  min={1}
                  max={14}
                  value={business.rules.catchUp}
                  onChange={(e) => rule('catchUp', Math.round(number(e.target.value, 3)))}
                />
              </Field>
            </div>
          </fieldset>

          {business.kinds.map((item) => {
            const key = (name: string) => `${item.id}-${name}`;
            return (
              <fieldset
                key={item.id}
                className="admin-panel content-section"
                disabled={readOnly}
                aria-label={item.label}
              >
                <h2 className="content-subtitle">
                  <span aria-hidden="true">{item.icon}</span> {item.label}
                  <span className="admin-muted"> · {item.id}</span>
                </h2>
                <div className="content-grid">
                  <Field id={key('label')} label="Name">
                    <Input
                      id={key('label')}
                      value={item.label}
                      maxLength={40}
                      onChange={(e) => kind(item.id, (draft) => void (draft.label = e.target.value))}
                    />
                  </Field>
                  <Field id={key('icon')} label="Icon">
                    <Input
                      id={key('icon')}
                      value={item.icon}
                      maxLength={16}
                      onChange={(e) => kind(item.id, (draft) => void (draft.icon = e.target.value))}
                    />
                  </Field>
                  <Field id={key('blurb')} label="One line about it">
                    <Input
                      id={key('blurb')}
                      value={item.blurb}
                      maxLength={120}
                      onChange={(e) => kind(item.id, (draft) => void (draft.blurb = e.target.value))}
                    />
                  </Field>
                  <Field id={key('setup')} label="Fit-out, stall (£)">
                    <Input
                      id={key('setup')}
                      type="number"
                      min={1}
                      step="0.01"
                      value={item.setupPence / 100}
                      onChange={(e) =>
                        kind(
                          item.id,
                          (draft) => void (draft.setupPence = Math.max(100, toPence(e.target.value, draft.setupPence))),
                        )
                      }
                    />
                  </Field>
                  <Field id={key('ticket')} label="A customer spends (£)">
                    <Input
                      id={key('ticket')}
                      type="number"
                      min={0.1}
                      step="0.01"
                      value={item.ticketPence / 100}
                      onChange={(e) =>
                        kind(
                          item.id,
                          (draft) =>
                            void (draft.ticketPence = Math.max(10, toPence(e.target.value, draft.ticketPence))),
                        )
                      }
                    />
                  </Field>
                  <Field id={key('margin')} label="Profit on a sale (%)">
                    <Input
                      id={key('margin')}
                      type="number"
                      min={1}
                      max={100}
                      value={Math.round(item.margin * 100)}
                      onChange={(e) =>
                        kind(item.id, (draft) => void (draft.margin = Math.max(0.01, number(e.target.value, 30) / 100)))
                      }
                    />
                  </Field>
                  <Field id={key('customers')} label="Customers an evening, stall">
                    <Input
                      id={key('customers')}
                      type="number"
                      min={0.1}
                      value={item.customers}
                      onChange={(e) =>
                        kind(item.id, (draft) => void (draft.customers = Math.max(0.1, number(e.target.value, 10))))
                      }
                    />
                  </Field>
                  <Field id={key('wage')} label="A week's wage (£)">
                    <Input
                      id={key('wage')}
                      type="number"
                      min={0}
                      step="0.01"
                      value={item.wagePence / 100}
                      onChange={(e) =>
                        kind(item.id, (draft) => void (draft.wagePence = toPence(e.target.value, draft.wagePence)))
                      }
                    />
                  </Field>
                  <Field id={key('rent')} label="A week's rent (% of fit-out)">
                    <Input
                      id={key('rent')}
                      type="number"
                      min={0.1}
                      step="0.1"
                      value={Math.round(item.rentShare * 1000) / 10}
                      onChange={(e) =>
                        kind(
                          item.id,
                          (draft) => void (draft.rentShare = Math.max(0.001, number(e.target.value, 5) / 100)),
                        )
                      }
                    />
                  </Field>
                  {(['min', 'usual', 'max'] as const).map((which) => (
                    <Field
                      key={which}
                      id={key(`staff-${which}`)}
                      label={`Staff, stall: ${which === 'min' ? 'fewest' : which === 'usual' ? 'usual' : 'most'}`}
                    >
                      <Input
                        id={key(`staff-${which}`)}
                        type="number"
                        min={which === 'min' ? 0 : 1}
                        value={item.staff[which]}
                        onChange={(e) =>
                          kind(
                            item.id,
                            (draft) => void (draft.staff[which] = Math.max(0, Math.round(number(e.target.value, 1)))),
                          )
                        }
                      />
                    </Field>
                  ))}
                </div>
                <p className="content-mini-title">How busy each day is (1 is an ordinary day)</p>
                <div className="content-needs">
                  {DAYS.map((day, index) => (
                    <label key={day}>
                      <span>{day}</span>
                      <Input
                        type="number"
                        min={0}
                        max={5}
                        step="0.05"
                        value={item.days[index]}
                        onChange={(e) =>
                          kind(item.id, (draft) => void (draft.days[index] = Math.max(0, number(e.target.value, 1))))
                        }
                      />
                    </label>
                  ))}
                </div>
                <label className="designer-check">
                  <Switch
                    checked={item.visible}
                    aria-label={`Players can start a ${item.label}`}
                    onCheckedChange={(on) => kind(item.id, (draft) => void (draft.visible = on))}
                  />{' '}
                  Players can start one
                </label>
                {!readOnly && business.kinds.length > 1 && (
                  <div className="admin-page-actions">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() =>
                        change((draft) => void (draft.kinds = draft.kinds.filter((entry) => entry.id !== item.id)))
                      }
                    >
                      Remove {item.label}
                    </Button>
                  </div>
                )}
              </fieldset>
            );
          })}
          {!readOnly && (
            <div className="admin-page-actions">
              <Button
                type="button"
                onClick={() =>
                  change((draft) => {
                    const base = structuredClone(DEFAULT_BUSINESS.kinds[0]!);
                    draft.kinds.push({
                      ...base,
                      id: idFor('New business', new Set(draft.kinds.map((entry) => entry.id)), 'business'),
                      label: 'New business',
                      icon: '🏢',
                      blurb: '',
                      visible: false,
                    });
                  })
                }
              >
                Add a kind of business
              </Button>
            </div>
          )}

          <fieldset className="admin-panel content-section" disabled={readOnly} aria-label="Premises">
            <h2 className="content-subtitle">Premises (compared with a stall)</h2>
            {PREMISES_KEYS.map((size) => {
              const premises = business.premises[size];
              const set = (fn: (draft: typeof premises) => void) => change((draft) => fn(draft.premises[size]));
              return (
                <div key={size} className="content-grid">
                  <Field id={`premises-${size}-label`} label={`${premises.icon} Name`}>
                    <Input
                      id={`premises-${size}-label`}
                      value={premises.label}
                      maxLength={30}
                      onChange={(e) => set((draft) => void (draft.label = e.target.value))}
                    />
                  </Field>
                  {(['customers', 'rent', 'setup', 'staff'] as const).map((field) => (
                    <Field
                      key={field}
                      id={`premises-${size}-${field}`}
                      label={`${field === 'setup' ? 'Fit-out' : field[0]!.toUpperCase() + field.slice(1)} ×`}
                    >
                      <Input
                        id={`premises-${size}-${field}`}
                        type="number"
                        min={field === 'staff' ? 1 : 0.1}
                        step="0.1"
                        value={premises[field]}
                        onChange={(e) =>
                          set(
                            (draft) =>
                              void (draft[field] = Math.max(field === 'staff' ? 1 : 0.1, number(e.target.value, 1))),
                          )
                        }
                      />
                    </Field>
                  ))}
                  <Field id={`premises-${size}-cap`} label="Most an evening (£)">
                    <Input
                      id={`premises-${size}-cap`}
                      type="number"
                      min={1}
                      value={premises.capPence / 100}
                      onChange={(e) =>
                        set((draft) => void (draft.capPence = Math.max(100, toPence(e.target.value, draft.capPence))))
                      }
                    />
                  </Field>
                  <Field id={`premises-${size}-stock`} label="Storeroom (items)">
                    <Input
                      id={`premises-${size}-stock`}
                      type="number"
                      min={1}
                      value={premises.stock ?? DEFAULT_STOCK[size]}
                      onChange={(e) =>
                        set(
                          (draft) =>
                            void (draft.stock = Math.max(1, Math.round(number(e.target.value, DEFAULT_STOCK[size])))),
                        )
                      }
                    />
                  </Field>
                </div>
              );
            })}
          </fieldset>

          <fieldset className="admin-panel content-section" disabled={readOnly} aria-label="Shelves">
            <h2 className="content-subtitle">Shelves: what shops sell other players</h2>
            <div className="content-grid">
              {(
                [
                  ['maxListed', 'Products on a shelf', 1],
                  ['maxQty', 'Most of one at a time', 1],
                  ['buyerAgeDays', 'Days old before a life can buy', 1],
                ] as const
              ).map(([key, label]) => (
                <Field key={key} id={`shelf-${key}`} label={label}>
                  <Input
                    id={`shelf-${key}`}
                    type="number"
                    min={key === 'buyerAgeDays' ? 0 : 1}
                    value={shelfRules[key]}
                    onChange={(e) =>
                      shelf(key, Math.max(key === 'buyerAgeDays' ? 0 : 1, Math.round(number(e.target.value, 1))))
                    }
                  />
                </Field>
              ))}
              <Field id="shelf-band-min" label="Lowest price (× usual)">
                <Input
                  id="shelf-band-min"
                  type="number"
                  step="0.05"
                  min={0.1}
                  max={1}
                  value={shelfRules.bandMin}
                  onChange={(e) => shelf('bandMin', Math.min(1, Math.max(0.1, number(e.target.value, 0.8))))}
                />
              </Field>
              <Field id="shelf-band-max" label="Highest price (× usual)">
                <Input
                  id="shelf-band-max"
                  type="number"
                  step="0.05"
                  min={1}
                  max={10}
                  value={shelfRules.bandMax}
                  onChange={(e) => shelf('bandMax', Math.min(10, Math.max(1, number(e.target.value, 2.5))))}
                />
              </Field>
              <Field id="shelf-shop-day" label="One shop sells a day, at most (£)">
                <Input
                  id="shelf-shop-day"
                  type="number"
                  min={1}
                  value={shelfRules.perShopDayPence / 100}
                  onChange={(e) =>
                    shelf('perShopDayPence', Math.max(100, toPence(e.target.value, shelfRules.perShopDayPence)))
                  }
                />
              </Field>
              <Field id="shelf-buyer-day" label="One player spends a day, at most (£)">
                <Input
                  id="shelf-buyer-day"
                  type="number"
                  min={1}
                  value={shelfRules.perBuyerDayPence / 100}
                  onChange={(e) =>
                    shelf('perBuyerDayPence', Math.max(100, toPence(e.target.value, shelfRules.perBuyerDayPence)))
                  }
                />
              </Field>
            </div>
          </fieldset>
          {products.map((item) => {
            const key = (name: string) => `product-${item.id}-${name}`;
            return (
              <fieldset
                key={item.id}
                className="admin-panel content-section"
                disabled={readOnly}
                aria-label={`Product: ${item.label}`}
              >
                <h2 className="content-subtitle">
                  <span aria-hidden="true">{item.icon}</span> {item.label}
                  <span className="admin-muted"> · product · {item.id}</span>
                </h2>
                <div className="content-grid">
                  <Field id={key('label')} label="Name">
                    <Input
                      id={key('label')}
                      value={item.label}
                      maxLength={40}
                      onChange={(e) => product(item.id, (draft) => void (draft.label = e.target.value))}
                    />
                  </Field>
                  <Field id={key('icon')} label="Icon">
                    <Input
                      id={key('icon')}
                      value={item.icon}
                      maxLength={16}
                      onChange={(e) => product(item.id, (draft) => void (draft.icon = e.target.value))}
                    />
                  </Field>
                  <Field id={key('ref')} label="Usual price (£)">
                    <Input
                      id={key('ref')}
                      type="number"
                      min={0.1}
                      step="0.01"
                      value={item.refPence / 100}
                      onChange={(e) =>
                        product(
                          item.id,
                          (draft) => void (draft.refPence = Math.max(10, toPence(e.target.value, draft.refPence))),
                        )
                      }
                    />
                  </Field>
                  <Field id={key('wear')} label="Changes their look">
                    <select
                      id={key('wear')}
                      className="content-select"
                      value={item.wear?.part ?? ''}
                      onChange={(e) =>
                        product(item.id, (draft) => {
                          const part = e.target.value as WearPart | '';
                          if (!part) delete draft.wear;
                          else
                            draft.wear = {
                              part,
                              value: optionsFor(part)[0]?.key ?? '',
                            };
                        })
                      }
                    >
                      <option value="">No</option>
                      {WEAR_PARTS.map((part) => (
                        <option key={part} value={part}>
                          {WEAR_PART_LABELS[part]}
                        </option>
                      ))}
                    </select>
                  </Field>
                  {item.wear && (
                    <Field id={key('wear-to')} label={`${WEAR_PART_LABELS[item.wear.part]} they get`}>
                      <select
                        id={key('wear-to')}
                        className="content-select"
                        value={item.wear.value}
                        onChange={(e) =>
                          product(item.id, (draft) => {
                            if (draft.wear) draft.wear.value = e.target.value;
                          })
                        }
                      >
                        {optionsFor(item.wear.part).map((option) => (
                          <option key={option.key} value={option.key}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </Field>
                  )}
                </div>
                <p className="content-mini-title">Sold by</p>
                <div className="content-needs">
                  {business.kinds.map((kind) => (
                    <label key={kind.id} className="designer-check">
                      <Switch
                        checked={item.kinds.includes(kind.id)}
                        aria-label={`${kind.label} sells ${item.label}`}
                        disabled={item.kinds.length === 1 && item.kinds.includes(kind.id)}
                        onCheckedChange={(on) =>
                          product(
                            item.id,
                            (draft) =>
                              void (draft.kinds = on
                                ? [...draft.kinds, kind.id]
                                : draft.kinds.filter((entry) => entry !== kind.id)),
                          )
                        }
                      />{' '}
                      {kind.icon} {kind.label}
                    </label>
                  ))}
                </div>
                <p className="content-mini-title">What it does to the one who buys it (−100 to 100)</p>
                <div className="content-needs">
                  {NEED_KEYS.map((need: NeedKey) => (
                    <label key={need}>
                      <span>
                        {NEEDS[need].icon} {NEEDS[need].label}
                      </span>
                      <Input
                        type="number"
                        min={-100}
                        max={100}
                        value={item.effects[need] ?? ''}
                        onChange={(e) =>
                          product(item.id, (draft) => {
                            const value = Math.round(number(e.target.value, 0));
                            if (value) draft.effects[need] = Math.max(-100, Math.min(100, value));
                            else delete draft.effects[need];
                          })
                        }
                      />
                    </label>
                  ))}
                </div>
                <label className="designer-check">
                  <Switch
                    checked={item.visible}
                    aria-label={`${item.label} can be sold`}
                    onCheckedChange={(on) => product(item.id, (draft) => void (draft.visible = on))}
                  />{' '}
                  Shops can sell it
                </label>
              </fieldset>
            );
          })}
          {!readOnly && (
            <div className="admin-page-actions">
              <Button
                type="button"
                variant="outline"
                onClick={() =>
                  changeProducts((list) =>
                    list.push({
                      id: idFor('New product', new Set(list.map((entry) => entry.id)), 'business'),
                      label: 'New product',
                      icon: '🛍️',
                      kinds: [business.kinds[0]!.id],
                      refPence: 500,
                      effects: { hunger: 20 },
                      visible: false,
                    }),
                  )
                }
              >
                Add a product
              </Button>
            </div>
          )}

          <fieldset className="admin-panel content-section" disabled={readOnly} aria-label="Areas">
            <h2 className="content-subtitle">Areas and their price tier (1 cheapest, 4 dearest)</h2>
            <div className="content-grid">
              {business.areas.map((area, index) => (
                <Field key={area.id} id={`area-${area.id}`} label={area.name}>
                  <select
                    id={`area-${area.id}`}
                    className="content-select"
                    value={area.tier}
                    onChange={(e) => change((draft) => void (draft.areas[index]!.tier = Number(e.target.value)))}
                  >
                    {[1, 2, 3, 4].map((tier) => (
                      <option key={tier} value={tier}>
                        Tier {tier}
                      </option>
                    ))}
                  </select>
                </Field>
              ))}
            </div>
          </fieldset>
        </div>
      )}
    </section>
  );
}
